import { useState } from 'react';
import { Button, Textarea, Text } from '@telegram-apps/telegram-ui';
import { useTelegram } from '../hooks/useTelegram';
import { RatingModal } from './RatingModal';
import type { DealWithDetails, ApprovalAction } from '../types';

interface ApprovalButtonsProps {
  deal: DealWithDetails;
  onAction: (action: ApprovalAction, body?: { message?: string; rating?: number; ratingComment?: string }) => Promise<boolean>;
  isLoading: boolean;
  error?: string | null;
  onRatingSuccess?: (rating: number) => void;
}

/**
 * Контекстные кнопки действий клиента
 * Рендерит разные кнопки в зависимости от clientAction сделки
 */
export function ApprovalButtons({ deal, onAction, isLoading, error, onRatingSuccess }: ApprovalButtonsProps) {
  const { showConfirm, showAlert, hapticFeedback } = useTelegram();
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [remarksText, setRemarksText] = useState('');
  const [remarksAction, setRemarksAction] = useState<ApprovalAction | null>(null);
  const [showRatingModal, setShowRatingModal] = useState(false);

  if (!deal.clientAction || deal.clientAction === 'review') {
    return null;
  }

  /**
   * Обработка позитивного действия (согласовать, принять)
   */
  async function handleApprove(action: ApprovalAction, confirmMessage: string) {
    const confirmed = await showConfirm(confirmMessage);
    if (!confirmed) return;

    hapticFeedback('medium');
    const success = await onAction(action);

    if (success) {
      hapticFeedback('success');
      await showAlert('Действие выполнено успешно');
    } else {
      hapticFeedback('error');
    }
  }

  /**
   * Открыть модал замечаний
   */
  function handleOpenRemarks(action: ApprovalAction) {
    setRemarksAction(action);
    setRemarksText('');
    setShowRemarksModal(true);
  }

  /**
   * Отправить замечания
   */
  async function handleSendRemarks() {
    if (!remarksText.trim() || !remarksAction) return;

    hapticFeedback('medium');
    const success = await onAction(remarksAction, { message: remarksText.trim() });

    if (success) {
      hapticFeedback('success');
      setShowRemarksModal(false);
      setRemarksText('');
      await showAlert('Замечания отправлены');
    } else {
      hapticFeedback('error');
    }
  }

  /**
   * Обработка отклонения стоимости (деструктивное действие)
   */
  async function handleRejectCost() {
    const confirmed = await showConfirm(
      'Вы уверены, что хотите отклонить стоимость? Сделка будет закрыта как "Не устроила цена".'
    );
    if (!confirmed) return;

    hapticFeedback('medium');
    const success = await onAction('reject_cost');

    if (success) {
      hapticFeedback('warning');
      await showAlert('Стоимость отклонена');
    } else {
      hapticFeedback('error');
    }
  }

  // ==========================================
  // Рендер кнопок по типу действия
  // ==========================================

  return (
    <div style={{ padding: '16px' }}>
      {/* Ошибка действия */}
      {error && (
        <Text style={{
          color: '#ff3b30',
          fontSize: '14px',
          textAlign: 'center',
          marginBottom: '12px',
          padding: '8px 12px',
          backgroundColor: 'rgba(255, 59, 48, 0.08)',
          borderRadius: '8px',
        }}>
          {error}
        </Text>
      )}

      {/* Согласование стоимости */}
      {deal.clientAction === 'approve_cost' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {deal.opportunity > 0 && (
            <Text style={{ textAlign: 'center', marginBottom: '8px', fontSize: '16px' }}>
              Стоимость: <strong>{deal.opportunity.toLocaleString('ru-RU')} руб.</strong>
            </Text>
          )}
          <Button
            size="l"
            stretched
            onClick={() => handleApprove('approve_cost', 'Согласовать стоимость проекта?')}
            loading={isLoading}
            disabled={isLoading}
          >
            Одобряю стоимость
          </Button>
          <Button
            size="l"
            stretched
            mode="outline"
            onClick={handleRejectCost}
            disabled={isLoading}
            style={{ color: '#ff3b30', borderColor: '#ff3b30' }}
          >
            Не устраивает цена
          </Button>
        </div>
      )}

      {/* Согласование ТЗ */}
      {deal.clientAction === 'approve_tz' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Button
            size="l"
            stretched
            onClick={() => handleApprove('approve_tz', 'Согласовать техническое задание?')}
            loading={isLoading}
            disabled={isLoading}
          >
            Согласую ТЗ
          </Button>
          <Button
            size="l"
            stretched
            mode="outline"
            onClick={() => handleOpenRemarks('reject_tz')}
            disabled={isLoading}
            style={{ color: '#ff9500', borderColor: '#ff9500' }}
          >
            Замечания по ТЗ
          </Button>
        </div>
      )}

      {/* Приёмка работы */}
      {deal.clientAction === 'accept_work' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Button
            size="l"
            stretched
            onClick={() => setShowRatingModal(true)}
            loading={isLoading}
            disabled={isLoading}
          >
            Принимаю работу
          </Button>
          <Button
            size="l"
            stretched
            mode="outline"
            onClick={() => handleOpenRemarks('send_remarks')}
            disabled={isLoading}
            style={{ color: '#ff9500', borderColor: '#ff9500' }}
          >
            Отправить замечания
          </Button>
        </div>
      )}

      {/* Модал оценки качества */}
      {showRatingModal && (
        <RatingModal
          isLoading={isLoading}
          onClose={() => setShowRatingModal(false)}
          onSubmit={async (rating, ratingComment) => {
            hapticFeedback('medium');
            const success = await onAction('accept_work', { rating, ratingComment });
            if (success) {
              hapticFeedback('success');
              setShowRatingModal(false);
              onRatingSuccess?.(rating);
            } else {
              hapticFeedback('error');
            }
          }}
        />
      )}

      {/* Модал замечаний */}
      {showRemarksModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'flex-end',
            zIndex: 1000,
          }}
          onClick={() => setShowRemarksModal(false)}
        >
          <div
            style={{
              width: '100%',
              backgroundColor: 'var(--tgui--bg_color, #fff)',
              borderRadius: '16px 16px 0 0',
              padding: '20px',
              maxHeight: '80vh',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Text weight="1" style={{ fontSize: '18px', marginBottom: '16px' }}>
              Опишите замечания
            </Text>

            <Textarea
              placeholder="Введите ваши замечания..."
              value={remarksText}
              onChange={(e) => setRemarksText(e.target.value)}
              style={{ minHeight: '120px', marginBottom: '16px' }}
            />

            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                size="l"
                stretched
                onClick={handleSendRemarks}
                loading={isLoading}
                disabled={!remarksText.trim() || isLoading}
              >
                Отправить
              </Button>
              <Button
                size="l"
                mode="outline"
                onClick={() => setShowRemarksModal(false)}
                disabled={isLoading}
              >
                Отмена
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
